import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] py-12" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Link>

        <div className="glass-card p-8 sm:p-12">
          <div className="flex items-center space-x-3 mb-2">
            <Shield className="w-6 h-6 text-[#00F0FF]" />
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Privacy Policy
            </h1>
          </div>
          <p className="text-[#A1A7B5] text-sm mb-8">Last Updated: April 15, 2026</p>

          <div className="space-y-8 text-[#C8CDD8] text-sm leading-relaxed">
            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Overview</h2>
              <p>Renew Labs respects your privacy. This Privacy Policy explains how we collect, use, and protect your information.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Information We Collect</h2>
              <p className="text-white font-medium mb-2">Personal Information:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5] mb-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Billing and shipping address</li>
                <li>Payment information (processed via third parties)</li>
              </ul>
              <p className="text-white font-medium mb-2">Usage Data:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5] mb-4">
                <li>IP address</li>
                <li>Device/browser type</li>
                <li>Site activity</li>
              </ul>
              <p className="text-white font-medium mb-2">Uploaded Data (Biomarker Feature):</p>
              <p className="text-[#A1A7B5] mb-2">If you use our lab upload feature, we may collect:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Blood panel data</li>
                <li>Uploaded files (PDFs, images)</li>
              </ul>
              <p className="mt-2">This data is used solely to provide educational insights.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. How We Use Information</h2>
              <p className="mb-2">We use your information to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Process orders and payments</li>
                <li>Provide customer support</li>
                <li>Improve our website and services</li>
                <li>Generate educational biomarker insights</li>
                <li>Communicate updates or promotions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Data Storage and Security</h2>
              <p>We implement reasonable safeguards to protect your data. However, no system is 100% secure, and you acknowledge this risk.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Data Sharing</h2>
              <p className="mb-3">We do not sell your data.</p>
              <p className="mb-2">We may share data with:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Payment processors (e.g., Stripe)</li>
                <li>Hosting providers</li>
                <li>Analytics tools</li>
              </ul>
              <p className="mt-2">These providers only access data necessary to perform their services.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Biomarker / Health Data Disclaimer</h2>
              <p className="mb-3">Our platform provides educational insights only.</p>
              <p className="mb-2">We are:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Not a healthcare provider</li>
                <li>Not subject to medical provider obligations in all cases</li>
                <li>Not responsible for decisions made based on insights</li>
              </ul>
              <p className="mt-3">You should consult a licensed professional before making health decisions.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Your Rights</h2>
              <p className="mb-2">You may:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Request access to your data</li>
                <li>Request deletion of your data</li>
                <li>Opt out of marketing communications</li>
              </ul>
              <p className="mt-3">Contact us at <a href="mailto:support@getrenewlabs.com" className="text-[#00F0FF] hover:underline">support@getrenewlabs.com</a> to exercise these rights.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Cookies</h2>
              <p className="mb-2">We use cookies to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-[#A1A7B5]">
                <li>Improve user experience</li>
                <li>Track usage</li>
                <li>Support analytics</li>
              </ul>
              <p className="mt-2">You can disable cookies via your browser settings.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Third-Party Links</h2>
              <p>We are not responsible for the privacy practices of third-party websites.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Changes to This Policy</h2>
              <p>We may update this Privacy Policy at any time. Continued use of the Site constitutes acceptance of updates.</p>
            </section>

            <section>
              <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Contact</h2>
              <p>Email: <a href="mailto:support@getrenewlabs.com" className="text-[#00F0FF] hover:underline">support@getrenewlabs.com</a></p>
              <p className="text-[#A1A7B5]">Address: 8074 Precinct Ln Rd, Colleyville, TX 76034</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
